import { useEffect, useRef, useState } from 'react';
import { Inline, LinkButton, Stack } from '@etg/wings';
import { useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import { CaretDown } from '@phosphor-icons/react';
import type { BoundType } from '../../../models';
import { useOrderDetails } from '../../../containers/OrderDetailsContext';
import BoundCard from './bound-card/BoundCard';

const showMoreIconStyles = (isOpen: boolean) => css`
  transform: ${isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  transition: transform 150ms ease;
  vertical-align: text-bottom;
`;

interface BoundListProps {
  ariaControls?: string;
  ariaExpanded?: 'true' | 'false';
  ariaHaspopup?: 'dialog' | 'menu' | 'listbox' | 'tree' | 'grid';
  handleBoundCardSelection: (bound: BoundType) => void;
}

const BoundCardList = ({
  handleBoundCardSelection,
  ariaControls,
  ariaExpanded,
  ariaHaspopup,
}: BoundListProps) => {
  const { t } = useTranslation();
  const { bounds } = useOrderDetails();
  const [isShowMoreSelected, setIsShowMoreSelected] = useState(false);
  const firstBoundCardRef = useRef<HTMLButtonElement | null>(null);
  const hasMoreThanThreeBounds = bounds.length > 3;
  const initiallyVisibleBounds = hasMoreThanThreeBounds ? bounds.slice(0, 2) : bounds;
  const initiallyHiddenBounds = hasMoreThanThreeBounds ? bounds.slice(2) : null;

  useEffect(() => {
    if (isShowMoreSelected && firstBoundCardRef.current) {
      firstBoundCardRef.current.focus();
    }
  }, [isShowMoreSelected]);

  return (
    <Stack>
      <Stack role="list">
        {initiallyVisibleBounds.map((bound, index) => (
          <BoundCard
            ariaControls={ariaControls}
            ariaExpanded={ariaExpanded}
            ariaHaspopup={ariaHaspopup}
            bound={bound}
            boundIndex={index}
            key={bound.boundId}
            onBoundCardSelection={() => handleBoundCardSelection(bound)}
          />
        ))}
        {isShowMoreSelected &&
          initiallyHiddenBounds?.map((bound, index) => (
            <BoundCard
              ariaControls={ariaControls}
              ariaExpanded={ariaExpanded}
              ariaHaspopup={ariaHaspopup}
              bound={bound}
              boundIndex={initiallyVisibleBounds.length + index}
              key={bound.boundId}
              onBoundCardSelection={() => handleBoundCardSelection(bound)}
              ref={index === 0 ? firstBoundCardRef : undefined}
            />
          ))}
      </Stack>
      {initiallyHiddenBounds && (
        <LinkButton
          data-testid="itinerary-overview-bound-cards-show-more-button"
          onClick={() => setIsShowMoreSelected((prev) => !prev)}
        >
          <Inline align="center" spacing={8}>
            {isShowMoreSelected ? t('ShowLess') : t('ShowMore')}
            <CaretDown className={showMoreIconStyles(isShowMoreSelected)} size={20} />
          </Inline>
        </LinkButton>
      )}
    </Stack>
  );
};

export default BoundCardList;
